import type {
  CopyTarget,
  DayFormValue,
  OpeningHoursFormValues,
  OpeningHoursModalProps,
  TabKey,
  WeekDay,
} from "../../../helpers/types";
import {
  CAccordion,
  CFormInput,
  CFormSelect,
  CModal,
  CModalContent,
  CModalHeader,
  CNav,
  CNavItem,
  CNavLink,
  CSpinner,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import DotIcon from "../../../components/icons/DotIcon";
import PlusIcon from "../../../components/icons/PlusIcon";
import TimeSelectInput from "../../../components/inputs/TimeSelectInput";
import {
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CFormCheck,
} from "@coreui/react";
import {
  COPY_OPTIONS,
  EMPTY_EXCEPTION,
  EMPTY_RANGE,
  STATUS_OPTIONS,
  TABS,
} from "../../../helpers/constants";
import { useState } from "react";
import { useOpeningHours } from "./useOpeningHours";
import { DAYS_OF_THE_WEEK } from "../../../config/constants";
import { general } from "../../../locales/general";

export function DayDisplayValue({ dayHours }: { dayHours?: DayFormValue }) {
  switch (dayHours?.status) {
    case "closed":
      return <div>Fermé</div>;
    case "openAllDay":
      return <div>Ouvert toute la journée</div>;
    case "custom":
      return (
        <div>
          {dayHours.ranges?.map((range, i) => (
            <div key={i}>
              {range.from} - {range.to}
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

interface Props {
  index: number;
  onRemove: () => void;
}

export function ExceptionDate({ index, onRemove }: Props) {
  const { register } = useFormContext<OpeningHoursFormValues>();
  const id = `exceptions-${index}`;

  return (
    <div className="d-flex justify-content-between border-bottom py-3">
      <div className="px-2" style={{ flex: 1 }}>
        <label htmlFor={`${id}-name`}>Nom</label>
        <CFormInput
          id={`${id}-name`}
          {...register(`exceptions.${index}.name`)}
        />
      </div>
      <div className="px-2" style={{ flex: 1 }}>
        <label htmlFor={`${id}-date`}>Date</label>
        <CFormInput
          id={`${id}-date`}
          type="date"
          {...register(`exceptions.${index}.date`)}
        />
      </div>
      <div className="px-2" style={{ flex: 1 }}>
        <label htmlFor={`${id}-repeating`}>Répétition annuelle</label>
        <CFormSelect
          id={`${id}-repeating`}
          className="mt-0"
          {...register(`exceptions.${index}.repeating`)}
        >
          <option value="">Choisissez une option</option>
          <option value="true">Oui</option>
          <option value="false">Non</option>
        </CFormSelect>
      </div>
      <div
        className="d-flex align-items-end justify-content-end"
        style={{ width: 45 }}
      >
        <button
          type="button"
          className="btn btn-danger btn-sm"
          onClick={onRemove}
          style={{ width: 41, height: 41 }}
        >
          <DeleteIcon width="18" height="18" fill="#ffffff" />
        </button>
      </div>
    </div>
  );
}

function RangeRow({
  day,
  index,
  onRemove,
}: {
  day: WeekDay;
  index: number;
  onRemove?: () => void;
}) {
  const { control } = useFormContext<OpeningHoursFormValues>();
  return (
    <div className="d-flex justify-content-between gap-3 mt-2">
      <div className="w-50">
        <Controller
          name={`${day}.ranges.${index}.from`}
          control={control}
          render={({ field }) => (
            <TimeSelectInput
              label="Heure d'ouverture"
              selectedTime={field.value}
              onChange={(val) => field.onChange(val)}
            />
          )}
        />
      </div>
      <div className="w-50 d-flex flex-column align-items-end">
        <Controller
          name={`${day}.ranges.${index}.to`}
          control={control}
          render={({ field }) => (
            <TimeSelectInput
              label="Heure de clôture"
              selectedTime={field.value}
              onChange={(val) => field.onChange(val)}
            />
          )}
        />
      </div>
      <div
        className="d-flex align-items-end justify-content-end"
        style={{ width: 45 }}
      >
        {onRemove && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={onRemove}
            style={{ width: 41, height: 41 }}
          >
            <DeleteIcon width="18" height="18" fill="#ffffff" />
          </button>
        )}
      </div>
    </div>
  );
}

interface DayTimeProps {
  day: WeekDay;
  label: string;
  onCopy: (target: CopyTarget, value: DayFormValue) => void;
}

export function DayTimeInput({ day, label, onCopy }: DayTimeProps) {
  const { control, register, watch, getValues } =
    useFormContext<OpeningHoursFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${day}.ranges`,
  });

  const dayHours = watch(day);
  const isCustom = dayHours?.status === "custom";

  return (
    <CAccordionItem itemKey={day}>
      <CAccordionHeader>
        <div className="w-100 d-flex justify-content-between pe-2">
          <div>{label}</div>
          <DayDisplayValue dayHours={dayHours} />
        </div>
      </CAccordionHeader>

      <CAccordionBody>
        <div className="position-relative openingHour-type-wrapper">
          <div className="d-lg-flex align-items-center justify-content-between gap-4">
            <div className="d-lg-flex align-items-center gap-4">
              {STATUS_OPTIONS.map((opt) => (
                <CFormCheck
                  key={opt.value}
                  type="radio"
                  id={`${day}-${opt.value}-openingHr`}
                  label={opt.label}
                  value={opt.value}
                  {...register(`${day}.status`)}
                />
              ))}
            </div>

            <CDropdown direction="center">
              <CDropdownToggle className="outline-none border-0 rounded-sm bg-transparent d-flex align-items-center gap-2">
                <DotIcon fill="black" width="18" />
              </CDropdownToggle>
              <CDropdownMenu>
                {COPY_OPTIONS.map(({ target, label: optLabel }) => (
                  <CDropdownItem
                    key={target}
                    type="button"
                    onClick={() => onCopy(target, getValues(day) ?? {})}
                  >
                    {optLabel}
                  </CDropdownItem>
                ))}
              </CDropdownMenu>
            </CDropdown>
          </div>

          {isCustom && (
            <div>
              {fields.map((field, index) => (
                <RangeRow
                  key={field.id}
                  day={day}
                  index={index}
                  onRemove={index > 0 ? () => remove(index) : undefined}
                />
              ))}
              <div className="mt-2 d-flex justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => append({ ...EMPTY_RANGE })}
                >
                  <PlusIcon width="18" height="18" fill="#ffffff" />
                </button>
              </div>
            </div>
          )}
        </div>
      </CAccordionBody>
    </CAccordionItem>
  );
}

export function OpeningHoursModal({
  visible,
  onClose,
  placeId,
}: OpeningHoursModalProps) {
  const [activeKey, setActiveKey] = useState<TabKey>(1);
  const { form, exceptions, onSubmit, copyTo, isSaving } = useOpeningHours({
    visible,
    placeId,
    onClose,
  });

  const handleClose = () => {
    onClose();
    setActiveKey(1);
    form.reset();
  };

  return (
    <CModal
      visible={Boolean(visible && placeId)}
      onClose={handleClose}
      size="lg"
      backdrop="static"
    >
      <CModalHeader>
        <h5 className="fs-5">Ajouter les horaires d'ouverture</h5>
      </CModalHeader>

      <CModalContent className="border-0 px-3 py-2">
        <FormProvider {...form}>
          <form onSubmit={onSubmit}>
            <CNav variant="tabs" role="tablist">
              {TABS.map((tab) => (
                <CNavItem key={tab.key} role="presentation">
                  <CNavLink
                    active={activeKey === tab.key}
                    component="button"
                    type="button"
                    role="tab"
                    aria-controls={`opening-hours-pane-${tab.key}`}
                    aria-selected={activeKey === tab.key}
                    onClick={() => setActiveKey(tab.key)}
                  >
                    {tab.label}
                  </CNavLink>
                </CNavItem>
              ))}
            </CNav>

            <CTabContent>
              <CTabPane
                id="opening-hours-pane-1"
                role="tabpanel"
                visible={activeKey === 1}
                className="pt-4"
              >
                <div className="row">
                  <CAccordion alwaysOpen>
                    {DAYS_OF_THE_WEEK.map((day) => (
                      <div key={day.en} className="col-12">
                        <DayTimeInput
                          day={day.en as WeekDay}
                          label={day.fr}
                          onCopy={copyTo}
                        />
                      </div>
                    ))}
                  </CAccordion>
                </div>
              </CTabPane>

              <CTabPane
                id="opening-hours-pane-2"
                role="tabpanel"
                visible={activeKey === 2}
              >
                {exceptions.fields.map((exception, index) => (
                  <ExceptionDate
                    key={exception.id}
                    index={index}
                    onRemove={() => exceptions.remove(index)}
                  />
                ))}
                <div className="d-flex justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm text-white"
                    onClick={() => exceptions.append({ ...EMPTY_EXCEPTION })}
                  >
                    Ajouter une date
                  </button>
                </div>
              </CTabPane>
            </CTabContent>

            <button
              type="submit"
              disabled={isSaving}
              className="mt-4 btn btn-danger shadow-primary w-100 p-2 d-flex align-items-center justify-content-center"
            >
              {isSaving ? (
                <>
                  <CSpinner size="sm" /> {general.fr.wait}
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
          </form>
        </FormProvider>
      </CModalContent>
    </CModal>
  );
}
